import type { FieldKey } from '@atlassian/jira-polaris-domain-field/src/field/types.tsx';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { ConnectionFailure } from '@atlassian/jira-polaris-remote-issue/src/services/jira/connection/types.tsx';
import type { IssueTypeFieldValue } from '@atlassian/jira-polaris-domain-field/src/field-types/issue-type/types.tsx';
import {
	createGetConnectedIssuesData,
	createGetConnectionFieldIssueIds,
} from '../../selectors/connection.tsx';
import { getLocalIssueIdToJiraId } from '../../selectors/issue-ids.tsx';
import type { Props, State } from '../../types.tsx';
import { isConnectionFieldValue } from '../../utils/field-mapping/connection/index.tsx';
import { updateConnectionsProperties } from '../common/connection/index.tsx';

export type ConnectIssue = {
	id: string;
	issueKey: string;
	summary: string;
	issueType: IssueTypeFieldValue;
};

const getFailedIssues = (
	failures: ConnectionFailure[],
	issues: ConnectIssue[],
	operation: 'create' | 'delete',
): ConnectIssue[] => {
	return failures
		.map(({ issueTo, error }) => {
			fireErrorAnalytics({
				meta: {
					id: `polaris.issue.action.updateIssueConnections.connection-${operation}`,
					teamName: 'JPD - Sirius',
				},
				error: new Error(error),
				sendToPrivacyUnsafeSplunk: true,
			});

			return issues.find(({ id }) => id === issueTo);
		})
		.filter(Boolean);
};

export const updateIssueConnections =
	({
		localIssueId,
		issuesToConnect = [],
		issuesToDisconnect = [],
	}: {
		localIssueId: LocalIssueId;
		issuesToConnect?: ConnectIssue[];
		issuesToDisconnect?: ConnectIssue[];
	}): Action<State, Props> =>
	async ({ setState, getState }, props) => {
		if (!issuesToConnect.length && !issuesToDisconnect.length) {
			return;
		}

		const { issuesRemote, onIssueUpdateFailed } = props;

		const state = getState();

		const optimisticProperties = updateConnectionsProperties({
			issuesToDisconnect,
			issuesToConnect,
			localIssueId,
			state,
			props,
		});

		const optimisticState = {
			...state,
			properties: optimisticProperties,
		};

		setState(optimisticState);

		let failedAddConnections: ConnectIssue[] | undefined;
		let failedDeleteConnections: ConnectIssue[] | undefined;

		const localIssueIdToJiraId = getLocalIssueIdToJiraId(state, props);
		const sourceIssueId = localIssueIdToJiraId[localIssueId];

		// bulk linking
		const addConnectionsPromise =
			issuesToConnect.length > 0
				? issuesRemote
						.createConnections({
							issueFrom: sourceIssueId,
							issueTo: issuesToConnect.map(({ id }) => id),
						})
						.then(({ failures }) => getFailedIssues(failures, issuesToConnect, 'create'))
				: Promise.resolve([]);

		// bulk unlinking
		const deleteConnectionsPromise =
			issuesToDisconnect.length > 0
				? issuesRemote
						.deleteConnections({
							issueFrom: sourceIssueId,
							issueTo: issuesToDisconnect.map(({ id }) => id),
						})
						.then(({ failures }) => getFailedIssues(failures, issuesToDisconnect, 'delete'))
				: Promise.resolve([]);

		try {
			[failedAddConnections, failedDeleteConnections] = await Promise.all([
				addConnectionsPromise,
				deleteConnectionsPromise,
			]);

			if (failedAddConnections.length || failedDeleteConnections.length) {
				throw new Error('Failed to update connections');
			}
		} catch (error) {
			// revert optimistic update
			const revertedProperties = updateConnectionsProperties({
				issuesToDisconnect: failedAddConnections ?? issuesToConnect,
				issuesToConnect: failedDeleteConnections ?? issuesToDisconnect,
				localIssueId,
				state: optimisticState,
				props,
			});

			setState({ properties: revertedProperties });

			if (error instanceof Error) {
				fireErrorAnalytics({
					meta: {
						id: 'polaris.issue.action.updateIssueConnections',
						teamName: 'JPD - Sirius',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				onIssueUpdateFailed(error);
			}
		}
	};

export const updateConnectionFieldValue =
	({
		fieldKey,
		localIssueId,
		newValue,
		removeValue,
	}: {
		localIssueId: LocalIssueId;
		newValue: unknown;
		removeValue: unknown;
		fieldKey: FieldKey;
	}): Action<State, Props> =>
	({ getState, dispatch }, props) => {
		const state = getState();

		if (newValue === undefined) {
			const currentConnections = createGetConnectionFieldIssueIds(fieldKey, localIssueId)(
				state,
				props,
			);

			if (!currentConnections.length) {
				return;
			}

			dispatch(
				updateIssueConnections({
					localIssueId,
					issuesToDisconnect: createGetConnectedIssuesData(currentConnections)(state, props),
				}),
			);

			return;
		}

		const localIssueIdToJiraId = getLocalIssueIdToJiraId(getState(), props);
		const issuesToConnect = Array.isArray(newValue) ? newValue.filter(isConnectionFieldValue) : [];
		const issuesToDisconnect = Array.isArray(removeValue)
			? removeValue.filter(isConnectionFieldValue)
			: [];

		// we don't want to connect issue to itself
		if (issuesToConnect.some((issue) => issue.id === localIssueIdToJiraId[localIssueId])) {
			return;
		}

		dispatch(
			updateIssueConnections({
				localIssueId,
				issuesToConnect: createGetConnectedIssuesData(issuesToConnect)(state, props),
				issuesToDisconnect: createGetConnectedIssuesData(issuesToDisconnect)(state, props),
			}),
		);
	};
